import { Icon, IconProps } from '@scale/scaleui';
import {
  Button,
  Dialog,
  Flex,
  TextField,
  FormField,
  Popover,
  FileUpload,
  Icon as RadixIcon,
  Tabs,
  Text,
} from '@scale/scaleui-radix';
import { useCurrentEditor } from '@tiptap/react';
import { Editor } from '@tiptap/core';
import useIsEditorEditable from '../../hooks/useIsEditorEditable';
import { SketchPicker } from 'react-color';
import { SetStateAction, useState } from 'react';
import { faArrowUpFromBracket } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

export function FormatButton({
  type,
  icon,
  onToggle,
}: {
  type: string;
  icon: IconProps['icon'];
  onToggle: (editor: Editor) => void;
}) {
  const { editor } = useCurrentEditor();
  const isEditable = useIsEditorEditable();

  if (!editor) return null;

  return (
    <div className="p-2">
      <Icon
        tooltip={type}
        tooltipProps={{ delayDuration: 0 }}
        selected={editor.isActive(type)}
        onClick={() => onToggle(editor)}
        disabled={!isEditable}
        icon={icon}
        size="sm"
      />
    </div>
  );
}

export const BoldButton = () => (
  <FormatButton
    type="bold"
    icon="bold"
    onToggle={editor => editor.chain().focus().toggleBoldWithDefaultToken().run()}
  />
);
export const ItalicButton = () => (
  <FormatButton
    type="italic"
    icon="italic"
    onToggle={editor => editor.chain().focus().toggleItalicWithDefaultToken().run()}
  />
);
export const StrikethroughButton = () => (
  <FormatButton
    type="strike"
    icon="strikethrough"
    onToggle={editor => editor.chain().focus().toggleStrike().run()}
  />
);
export const InlineCodeButton = () => (
  <FormatButton
    type="code"
    icon="code-simple"
    onToggle={editor => editor.chain().focus().toggleCode().run()}
  />
);

export const LinkButton = () => {
  const { editor } = useCurrentEditor();
  const [showForm, setShowForm] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');

  if (!editor) return null;

  const onDone = () => {
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setShowForm(false);
  };

  const onRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setShowForm(false);
  };

  const onToggle = () => {
    if (showForm) {
      setShowForm(false);
      return;
    }
    setUrl(editor.getAttributes('link')?.href || '');
    setShowForm(true);
  };

  return (
    <Dialog.Root open={showForm} onOpenChange={setShowForm}>
      <Dialog.Trigger>
        <FormatButton type="link" icon="link" onToggle={onToggle} />
      </Dialog.Trigger>
      <Dialog.Content>
        <Dialog.Title>Link</Dialog.Title>
        <Flex direction="column" gap="3">
          <FormField.Root size="1">
            <FormField.Label>URL</FormField.Label>
            <TextField.Root value={url} onChange={e => setUrl(e.target.value)} />
          </FormField.Root>
          <Flex direction="row" gap="3" justify="end">
            <Button variant="outline" onClick={onRemoveLink}>
              Remove
            </Button>
            <Button onClick={onDone}>Done</Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export const UnderlineButton = () => (
  <FormatButton
    type="underline"
    icon="underline"
    onToggle={editor => editor.chain().focus().toggleUnderline().run()}
  />
);

export const TextAlignButton = () => {
  const { editor } = useCurrentEditor();
  const [showPopover, setShowPopover] = useState<boolean>(false);

  if (!editor) return null;

  const alignmentOptions = [
    { value: 'left', icon: 'align-left', label: 'Align Left' },
    { value: 'center', icon: 'align-center', label: 'Align Center' },
    { value: 'right', icon: 'align-right', label: 'Align Right' },
    { value: 'justify', icon: 'align-justify', label: 'Justify' },
  ];

  const currentAlignment = editor.getAttributes('textStyle')?.textAlign || 'left';

  return (
    <Popover.Root open={showPopover} onOpenChange={setShowPopover}>
      <Popover.Trigger>
        <Flex p="2">
          <Icon
            tooltip="Text Align"
            icon="indent"
            size="sm"
            selected={currentAlignment !== 'left'}
            onClick={() => setShowPopover(!showPopover)}
          />
        </Flex>
      </Popover.Trigger>
      <Popover.Content size="1">
        <Flex direction="column" gap="2">
          {alignmentOptions.map((option, index) => (
            <Flex key={index} justify="start">
              <Button
                key={index}
                color={currentAlignment === option.value ? 'accent' : 'neutral'}
                variant="ghost"
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .setTextAlign(option.value as 'left' | 'center' | 'right' | 'justify')
                    .run();
                  setShowPopover(false);
                }}
              >
                <Flex gap="2" align="center">
                  <Icon icon={option.icon as IconProp} size="sm" />
                  <Text size="1">{option.label}</Text>
                </Flex>
              </Button>
            </Flex>
          ))}
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
};

export const ImageButton = () => {
  enum ImageTab {
    Url = 'URL',
    FileUpload = 'File Upload',
  }
  const { editor } = useCurrentEditor();
  const [showForm, setShowForm] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');
  const [alt, setAlt] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [tab, setTab] = useState<ImageTab>(ImageTab.Url);

  if (!editor) return null;

  const onDone = () => {
    if (tab === ImageTab.FileUpload && uploadedFile) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        editor.chain().focus().setImage({ src: base64, alt }).run();
      };
      reader.readAsDataURL(uploadedFile);
    } else if (tab === ImageTab.Url && url) {
      editor.chain().focus().setImage({ src: url, alt }).run();
    } else {
      editor.chain().focus().deleteSelection().run();
    }
    setShowForm(false);
  };

  const onToggle = () => {
    if (showForm) {
      setShowForm(false);
      return;
    }
    setUrl('');
    setAlt('');
    setTab(ImageTab.Url);
    setUploadedFile(null);
    setShowForm(true);
  };

  return (
    <Dialog.Root open={showForm} onOpenChange={setShowForm}>
      <Dialog.Trigger>
        <Flex p="2">
          <Icon tooltip="Image" icon="image" size="sm" onClick={onToggle} />
        </Flex>
      </Dialog.Trigger>
      <Dialog.Content>
        <Dialog.Title>Image</Dialog.Title>
        <Flex direction="column" gap="3">
          <Tabs.Root value={tab} onValueChange={value => setTab(value as ImageTab)}>
            <Tabs.List size="1">
              {Object.values(ImageTab).map((tabValue, index) => (
                <Tabs.Trigger key={index} value={tabValue}>
                  <Text size="1">{tabValue}</Text>
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs.Root>
          {tab === ImageTab.Url && (
            <FormField.Root size="1">
              <FormField.Label>URL</FormField.Label>
              <TextField.Root value={url} onChange={e => setUrl(e.target.value)} />
            </FormField.Root>
          )}
          {tab === ImageTab.FileUpload && (
            <FormField.Root size="1">
              <FormField.Label>Upload image</FormField.Label>
              <FileUpload.Root
                maxSize={10 * 1024 * 1024} // 10 MB
                accept={{ 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] }}
                onFilesChange={(files: SetStateAction<File | null>[]) => setUploadedFile(files[0])}
              >
                <Flex direction="column" gap="2">
                  <Flex gap="2" align="center">
                    <FileUpload.Button>
                      <RadixIcon color="neutral" icon={faArrowUpFromBracket} label="Upload" />
                      <Text size="1">Upload File</Text>
                    </FileUpload.Button>
                    <FileUpload.Info />
                  </Flex>
                  <FileUpload.Files />
                </Flex>
              </FileUpload.Root>
            </FormField.Root>
          )}
          <FormField.Root size="1">
            <FormField.Label>Alt text</FormField.Label>
            <TextField.Root value={alt} onChange={e => setAlt(e.target.value)} />
          </FormField.Root>
          <Flex direction="row" gap="3" justify="end">
            <Button onClick={onDone}>Done</Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export const ColorButton = () => {
  const { editor } = useCurrentEditor();
  const [showPicker, setShowPicker] = useState<boolean>(false);

  if (!editor) return null;

  return (
    <Dialog.Root open={showPicker} onOpenChange={setShowPicker}>
      <Dialog.Trigger>
        <FormatButton type="color" icon="palette" onToggle={() => setShowPicker(!showPicker)} />
      </Dialog.Trigger>
      <Dialog.Content style={{ width: 'fit-content' }}>
        <Dialog.Title>Text color</Dialog.Title>
        <Flex direction="column" gap="3">
          <SketchPicker
            width="300px"
            color={editor.getAttributes('textStyle').color || '#000000FF'}
            onChange={newColor => {
              const { r, g, b, a } = newColor.rgb;
              const rgbaString = `rgba(${r}, ${g}, ${b}, ${a})`;
              editor.chain().focus().setColor(rgbaString).run();
            }}
          />
          <Flex direction="row" gap="3" justify="end">
            <Button variant="outline" onClick={() => editor.chain().focus().unsetColor().run()}>
              Reset
            </Button>
            <Button onClick={() => setShowPicker(false)}>Done</Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export const HighlightButton = () => {
  const { editor } = useCurrentEditor();
  const [showPicker, setShowPicker] = useState<boolean>(false);

  if (!editor) return null;

  return (
    <Dialog.Root open={showPicker} onOpenChange={setShowPicker}>
      <Dialog.Trigger>
        <FormatButton
          type="highlight"
          icon="highlighter"
          onToggle={() => setShowPicker(!showPicker)}
        />
      </Dialog.Trigger>
      <Dialog.Content style={{ width: 'fit-content' }}>
        <Dialog.Title>Highlight color</Dialog.Title>
        <Flex direction="column" gap="3">
          <SketchPicker
            disableAlpha
            width="300px"
            color={editor.getAttributes('highlight')?.color || '#FFFF00'}
            onChange={newColor => {
              const { r, g, b } = newColor.rgb;
              const rgbString = `rgb(${r}, ${g}, ${b})`;
              editor.chain().focus().toggleHighlight({ color: rgbString }).run();
            }}
          />
          <Flex direction="row" gap="3" justify="end">
            <Button variant="outline" onClick={() => editor.chain().focus().unsetHighlight().run()}>
              Reset
            </Button>
            <Button onClick={() => setShowPicker(false)}>Done</Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export const FontFamilyButton = () => {
  const { editor } = useCurrentEditor();
  const [showPopover, setShowPopover] = useState<boolean>(false);

  if (!editor) return null;

  const fontOptions = [
    { value: 'sans-serif', label: 'Sans-serif' },
    { value: 'serif', label: 'Serif' },
    { value: '"comic sans ms"', label: 'Comic Sans' },
    { value: 'monospace', label: 'Monospace' },
    { value: 'cursive', label: 'Cursive' },
  ];

  const currentFontFamily = editor.getAttributes('textStyle')?.fontFamily;
  return (
    <Popover.Root open={showPopover} onOpenChange={setShowPopover}>
      <Popover.Trigger>
        <Flex p="2">
          <Icon
            tooltip="Font"
            icon="font"
            size="sm"
            selected={currentFontFamily}
            onClick={() => setShowPopover(!showPopover)}
          />
        </Flex>
      </Popover.Trigger>
      <Popover.Content size="1">
        <Flex direction="column" gap="2">
          {fontOptions.map((option, index) => (
            <Button
              key={index}
              color={(currentFontFamily || 'sans-serif') === option.value ? 'accent' : 'neutral'}
              variant="ghost"
              onClick={() => {
                if (option.value === 'sans-serif') {
                  editor.chain().focus().unsetFontFamily().run();
                } else {
                  editor.chain().focus().setFontFamily(option.value).run();
                }
                setShowPopover(false);
              }}
              style={{ fontFamily: option.value }}
            >
              {option.label}
            </Button>
          ))}
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
};

export const YoutubeButton = () => {
  const { editor } = useCurrentEditor();
  const [showForm, setShowForm] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');
  const [width, setWidth] = useState<number>(100);
  const [height, setHeight] = useState<number>(500);
  const [widthError, setWidthError] = useState<string>('');
  const [heightError, setHeightError] = useState<string>('');

  const validateWidth = (value: number) => {
    if (value < 10 || value > 100) {
      setWidthError('Width must be between 10% and 100%.');
    } else {
      setWidthError('');
    }
  };

  const validateHeight = (value: number) => {
    if (value < 100 || value > 500) {
      setHeightError('Height must be between 100px and 500px.');
    } else {
      setHeightError('');
    }
  };

  if (!editor) return null;

  const onDone = () => {
    if (url) {
      editor
        .chain()
        .focus()
        .setYoutubeVideoWithStringParams({ src: url, width: `${width}%`, height: `${height}` })
        .run();
    } else {
      editor.chain().focus().deleteSelection().run();
    }
    setShowForm(false);
  };

  const onToggle = () => {
    if (showForm) {
      setShowForm(false);
      return;
    }
    const attributes = editor.getAttributes('youtube') || {};
    setUrl(attributes.src || '');
    setWidth(parseInt(attributes.width?.replace('%', '')) || 100);
    setHeight(parseInt(attributes.height) || 500);
    setWidthError('');
    setHeightError('');
    setShowForm(true);
  };

  return (
    <Dialog.Root open={showForm} onOpenChange={setShowForm}>
      <Dialog.Trigger>
        <Flex p="2">
          <Icon
            tooltip="YouTube Video"
            icon="video"
            size="sm"
            selected={editor.isActive('youtube')}
            onClick={onToggle}
          />
        </Flex>
      </Dialog.Trigger>
      <Dialog.Content>
        <Dialog.Title>YouTube Video</Dialog.Title>
        <Flex direction="column" gap="3">
          <FormField.Root size="1">
            <FormField.Label>URL</FormField.Label>
            <TextField.Root value={url} onChange={e => setUrl(e.target.value)} />
          </FormField.Root>
          <FormField.Root size="1">
            <FormField.Label>Width</FormField.Label>
            <Flex align="center" gap="2">
              <TextField.Root
                type="number"
                value={width}
                onChange={e => {
                  const value = parseInt(e.target.value) || 0;
                  setWidth(value);
                  validateWidth(value);
                }}
              />
              %
            </Flex>
            {widthError && (
              <FormField.Description color="error">{widthError}</FormField.Description>
            )}
          </FormField.Root>
          <FormField.Root size="1">
            <FormField.Label>Height</FormField.Label>
            <Flex align="center" gap="2">
              <TextField.Root
                type="number"
                value={height}
                onChange={e => {
                  const value = parseInt(e.target.value) || 0;
                  setHeight(value);
                  validateHeight(value);
                }}
              />
              px
            </Flex>
            {heightError && (
              <FormField.Description color="error">{heightError}</FormField.Description>
            )}
          </FormField.Root>
          <Flex direction="row" gap="3" justify="end">
            <Button onClick={onDone} disabled={!!widthError || !!heightError}>
              Done
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export const IFrameButton = () => {
  const { editor } = useCurrentEditor();
  const [showForm, setShowForm] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');
  const [width, setWidth] = useState<number>(100);
  const [height, setHeight] = useState<number>(500);

  if (!editor) return null;

  const onDone = () => {
    if (url) {
      editor
        .chain()
        .focus()
        .setIframe({ src: url, width: `${width}%`, height: `${height}px` })
        .run();
    } else {
      editor.chain().focus().deleteSelection().run();
    }
    setShowForm(false);
  };

  const onToggle = () => {
    if (showForm) {
      setShowForm(false);
      return;
    }
    const attributes = editor.getAttributes('iframe') || {};
    setUrl(attributes.src || '');
    setWidth(parseInt(attributes.width) || 100);
    setHeight(parseInt(attributes.height) || 500);
    setShowForm(true);
  };

  return (
    <Dialog.Root open={showForm} onOpenChange={setShowForm}>
      <Dialog.Trigger>
        <Flex p="2">
          <Icon
            tooltip="Embed Website"
            icon="browser"
            size="sm"
            selected={editor.isActive('iframe')}
            onClick={onToggle}
          />
        </Flex>
      </Dialog.Trigger>
      <Dialog.Content>
        <Dialog.Title>Embed Website</Dialog.Title>
        <Flex direction="column" gap="3">
          <FormField.Root size="1">
            <FormField.Label>URL</FormField.Label>
            <TextField.Root value={url} onChange={e => setUrl(e.target.value)} />
          </FormField.Root>
          <FormField.Root size="1">
            <FormField.Label>Width</FormField.Label>
            <Flex align="center" gap="2">
              <TextField.Root
                type="number"
                value={width}
                onChange={e => {
                  const value = Math.min(100, Math.max(10, parseInt(e.target.value) || 100));
                  setWidth(value);
                }}
              />
              %
            </Flex>
          </FormField.Root>
          <FormField.Root size="1">
            <FormField.Label>Height</FormField.Label>
            <Flex align="center" gap="2">
              <TextField.Root
                type="number"
                value={height}
                onChange={e => {
                  const value = Math.min(1000, Math.max(100, parseInt(e.target.value) || 500));
                  setHeight(value);
                }}
              />
              px
            </Flex>
          </FormField.Root>
          <Flex direction="row" gap="3" justify="end">
            <Button onClick={onDone}>Done</Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export const ClearFormattingButton = () => {
  const { editor } = useCurrentEditor();
  if (!editor) return null;

  return (
    <FormatButton
      type="Clear Text Formatting"
      icon="eraser"
      onToggle={editor => editor.chain().focus().unsetAllMarks().run()}
    />
  );
};

export const FontSizeButton = () => {
  const { editor } = useCurrentEditor();
  const [showPopover, setShowPopover] = useState<boolean>(false);

  if (!editor) return null;

  const fontSizeOptions = [
    ...Array.from({ length: 20 }, (_, i) => {
      const size = 8 + i * 4;
      return { value: `${size}px`, label: `${size}px` };
    }),
    { value: 'unset', label: 'Unset' },
  ];

  const getFontSizesInSelection = () => {
    const { from, to } = editor.state.selection;
    const fontSizes = new Set<string>();

    editor.state.doc.nodesBetween(from, to, node => {
      const fontSizesInNode = node.marks?.map(mark => mark.attrs.fontSize) || [];
      fontSizesInNode.forEach(fontSize => {
        if (fontSize) fontSizes.add(fontSize);
      });
    });
    return fontSizes;
  };

  const fontSizes = getFontSizesInSelection();
  const isMixedFontSize = fontSizes.size > 1;

  return (
    <Popover.Root open={showPopover} onOpenChange={setShowPopover}>
      <Popover.Trigger>
        <Flex p="2">
          <Icon
            tooltip="Font Size"
            icon="text-size"
            size="sm"
            selected={fontSizes.size > 0}
            onClick={() => setShowPopover(!showPopover)}
          />
        </Flex>
      </Popover.Trigger>
      <Popover.Content size="1">
        <Flex direction="column" gap="2">
          {fontSizeOptions.map((option, index) => (
            <Button
              key={index}
              color={isMixedFontSize || !fontSizes.has(option.value) ? 'neutral' : 'accent'}
              variant="ghost"
              onClick={() => {
                if (option.value === 'unset') {
                  editor.chain().focus().unsetFontSize().run();
                } else {
                  editor.chain().focus().setFontSize(option.value).run();
                }
                setShowPopover(false);
              }}
            >
              {option.label}
            </Button>
          ))}
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
};
